
import React from 'react';
import { useParams, useNavigate } from 'react-router';
import { 
    Zap, ShieldCheck, Sparkles, Search, Brain, 
    Beaker, Layout, Check, ChevronRight, X,
    Lock, Star, Info, Target, ArrowRight, ShieldAlert,
    Clock, Smartphone, Globe, Activity
} from 'lucide-react';
import { useAuth } from '../components/AuthBridge';
import { SponsoredService } from '../services/sponsoredService';
import { getSafeEmbedUrl } from '../services/embedService';

export const EliteUpgrade: React.FC = () => {
    const { certId } = useParams<{ certId: string }>();
    const navigate = useNavigate();
    const { user, logout } = useAuth();
    const [config, setConfig] = React.useState(SponsoredService.getConfig());

    React.useEffect(() => {
        SponsoredService.fetchLatestConfig().then(latest => {
            if (latest) {
                setConfig(latest);
            }
        });
    }, []);

    const handleUpgradeNow = async () => {
        const checkoutUrl = config.eliteProUrl || 'https://www.diamondnursingschool.com/plans-pricing';
        
        // Open the payment page in a new window/tab
        try {
            window.open(checkoutUrl, '_blank');
        } catch (e) {
            console.error("Popup blocked or failed to open.", e);
        }

        // Logout user so they can log back in of payment status verification
        if (logout) {
            await logout();
        }
    };

    const benefits = [
        {
            title: "Zero Interruptions",
            desc: "100% removal of all intermission ads and study-break banners across the entire hub.",
            icon: Zap,
            color: "text-amber-500",
            bg: "bg-amber-50"
        },
        {
            title: "Registry Research AI",
            desc: "Unlock the live-grounded AI Search Engine connected directly to state board handbooks.",
            icon: Search,
            color: "text-indigo-500",
            bg: "bg-indigo-50"
        },
        {
            title: "Priority Instructor Logic",
            desc: "Your Clinical Instructor uses higher-fidelity reasoning for complex protocol queries.",
            icon: Brain,
            color: "text-medical-500",
            bg: "bg-medical-50"
        },
        {
            title: "Advanced Math Laboratory",
            desc: "Full access to complex clinical metrics and infinite randomized simulation scenarios.",
            icon: Beaker,
            color: "text-rose-500",
            bg: "bg-rose-50"
        },
        {
            title: "Streak Shields",
            desc: "Automatically receive 5 Registry Shields per month to protect your learning momentum.",
            icon: ShieldCheck,
            color: "text-emerald-500",
            bg: "bg-emerald-50"
        },
        {
            title: "Elite Export Mode",
            desc: "Print modules as refined study packets with AI-optimized high-resolution diagrams.",
            icon: Layout,
            color: "text-purple-500",
            bg: "bg-purple-50"
        }
    ];

    const comparison = [
        { feature: "Full Curriculum Access", free: true, pro: true },
        { feature: "Standard Practice Exams", free: true, pro: true },
        { feature: "Audio Lessons (Podcasts)", free: true, pro: true },
        { feature: "No Intermission Ads", free: false, pro: true },
        { feature: "AI Registry Search Engine", free: false, pro: true },
        { feature: "Infinite Scenario Generation", free: false, pro: true },
        { feature: "Priority AI Support", free: false, pro: true },
        { feature: "Monthly Streak Shields", free: "0", pro: "5 Units" },
        { feature: "Advanced PDF Exports", free: false, pro: true },
    ];

    return (
        <div className="max-w-6xl mx-auto py-8 md:py-16 animate-fade-in text-slate-900 dark:text-white">
            {/* Header / Hero */}
            <div className="bg-slate-950 rounded-[3rem] md:rounded-[4rem] p-10 md:p-20 text-white shadow-2xl relative overflow-hidden group border border-white/5 mb-16">
                <div className="absolute top-0 right-0 w-full h-full bg-[radial-gradient(circle_at_top_right,rgba(99,102,241,0.15)_0%,transparent_60%)]"></div>
                <div className="relative z-10 flex flex-col items-center text-center max-w-4xl mx-auto">
                    <div className="flex items-center gap-3 px-6 py-2 bg-white/5 rounded-full border border-white/10 mb-10 animate-pop">
                        <Sparkles size={16} className="text-indigo-400" />
                        <span className="text-[10px] font-black tracking-[0.4em] text-slate-400">Identity Upgrade Protocol</span>
                    </div>
                    
                    <h1 className="text-4xl md:text-7xl font-black tracking-tighter leading-[0.9] mb-8">
                        Transition to <br/>
                        <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-400 via-medical-400 to-emerald-400 animate-pulse">Clinical Excellence.</span>
                    </h1>
                    
                    <p className="text-slate-400 text-lg md:text-2xl font-medium leading-relaxed opacity-95 mb-8">
                        "Unlock the full logic suite designed for high-performing candidates seeking board certification with zero compromise."
                    </p>

                    <div className="flex flex-col items-center gap-2 mb-12 bg-white/5 border border-white/10 px-8 py-5 rounded-[2rem] backdrop-blur-md">
                        <span className="text-slate-400 text-[9px] font-black tracking-widest uppercase">Dynamic Registry Access Fee</span>
                        <div className="text-4xl md:text-5xl font-black text-emerald-400 tracking-tight flex items-center justify-center gap-2">
                            <span>{config.eliteProPrice || '$49'}</span>
                            <span className="text-xs text-slate-400 font-extrabold tracking-wider uppercase">/ One-time Sync Payment</span>
                        </div>
                    </div>

                    <div className="flex flex-col sm:flex-row gap-6 w-full sm:w-auto">
                        <button 
                            onClick={handleUpgradeNow}
                            className="px-12 py-6 bg-white text-slate-950 rounded-[1.5rem] md:rounded-3xl font-black text-sm tracking-widest shadow-[0_20px_50px_rgba(255,255,255,0.15)] hover:bg-emerald-50 transition-all active:scale-95 border-b-4 border-slate-200 group"
                        >
                            Upgrade Now <ArrowRight size={20} className="inline ml-3 group-hover:translate-x-1 transition-transform" />
                        </button>
                        <button 
                            onClick={() => navigate(`/${certId}/dashboard`)}
                            className="px-12 py-6 bg-white/5 text-white border border-white/10 rounded-[1.5rem] md:rounded-3xl font-black text-sm tracking-widest hover:bg-white/10 transition-all active:scale-95"
                        >
                            Explore Standard
                        </button>
                    </div>
                </div>
            </div>

            {/* Free Core Curriculum Guarantee Alert Box */}
            <div id="free-access-guarantee-banner" className="bg-emerald-500/[0.04] dark:bg-emerald-500/[0.02] border border-emerald-500/20 p-6 md:p-8 rounded-[2.5rem] shadow-sm mb-16 flex flex-col sm:flex-row items-center sm:items-start gap-4 md:gap-6 animate-fade-in">
                <div className="p-3.5 bg-emerald-500/10 text-emerald-650 dark:text-emerald-400 rounded-2xl shrink-0 self-start">
                    <ShieldCheck size={28} className="text-emerald-600 dark:text-emerald-450" />
                </div>
                <div className="space-y-2 text-center sm:text-left">
                    <div className="flex flex-wrap items-center justify-center sm:justify-start gap-2">
                        <h3 className="text-sm md:text-base font-black uppercase tracking-widest text-emerald-900 dark:text-emerald-400">
                            Core Study Content is 100% Free & Unlimited
                        </h3>
                        <span className="px-2 py-0.5 bg-emerald-500/10 text-emerald-700 dark:text-emerald-450 font-black text-[8px] tracking-widest uppercase rounded">
                            Guarantee
                        </span>
                    </div>
                    <p className="text-slate-650 dark:text-slate-300 text-xs md:text-sm font-semibold leading-relaxed">
                        To support all nursing candidates, <span className="font-extrabold text-slate-900 dark:text-white">Full Curriculum Access, Standard Practice Exams, Course Lessons, and Audio Podcasts remain 100% FREE, unlimited, and unrestricted</span> for all standard users. Upgrading to Elite Pro is completely optional and strictly unlocks productivity accelerators (such as ad-free study, AI search tools, and scenario simulation templates). You do not need to upgrade to fully prepare for, study, and pass your state board exams!
                    </p>
                </div>
            </div>

            {/* Video Showcase Section */}
            {config.eliteProVideoUrl && (
                <div className="bg-slate-950 rounded-[3rem] p-8 md:p-16 border border-white/5 shadow-2xl mb-16 text-center text-white relative overflow-hidden group">
                    <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(circle_at_bottom_left,rgba(99,102,241,0.1)_0%,transparent_50%)]"></div>
                    <div className="max-w-3xl mx-auto space-y-8 relative z-10">
                        <div>
                            <span className="px-5 py-1.5 bg-indigo-505/20 text-indigo-300 rounded-full text-[10px] font-black tracking-[0.25em] uppercase border border-indigo-400/20">Video Walkthrough</span>
                            <h2 className="text-3xl md:text-4xl font-black tracking-tight mt-4">Feature Presentation & Tour</h2>
                            <p className="text-slate-400 text-xs md:text-sm font-medium mt-1">Take an immersive visual walkthrough of CNA study simulations.</p>
                        </div>

                        <div className="relative aspect-video w-full rounded-[2rem] overflow-hidden border border-white/10 bg-black shadow-2xl">
                            {getSafeEmbedUrl(config.eliteProVideoUrl) ? (
                                <iframe 
                                    className="absolute top-0 left-0 w-full h-full"
                                    src={getSafeEmbedUrl(config.eliteProVideoUrl)}
                                    title="Elite Pro Walkthrough"
                                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                                    referrerPolicy="strict-origin-when-cross-origin"
                                    allowFullScreen
                                ></iframe>
                            ) : (
                                <div className="absolute inset-0 flex flex-col items-center justify-center p-6 space-y-6">
                                    <div className="w-20 h-20 rounded-full bg-indigo-600 flex items-center justify-center text-white font-bold text-4xl shadow-lg animate-pulse">▶</div>
                                    <div>
                                        <p className="text-white font-black text-lg">Official Showcase Presentation Available</p>
                                        <p className="text-slate-400 text-xs font-semibold mt-1">Review full CNA Elite suite capabilities in a new viewport.</p>
                                    </div>
                                    <a 
                                        href={config.eliteProVideoUrl} 
                                        target="_blank" 
                                        rel="noopener noreferrer" 
                                        className="px-8 py-4 bg-indigo-600 hover:bg-indigo-500 text-white rounded-xl font-black text-xs tracking-widest uppercase shadow-xl transition-all inline-flex items-center gap-2"
                                    >
                                        Launch Showcase Presentation <ArrowRight size={14} />
                                    </a>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Value Pillars */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-24">
                {benefits.map((b, i) => (
                    <div key={i} className="bg-white dark:bg-slate-900 p-8 md:p-10 rounded-[2.5rem] border border-slate-200 dark:border-slate-800 shadow-sm hover:shadow-2xl hover:border-indigo-500 dark:hover:border-indigo-500 transition-all duration-300 group">
                        <div className={`w-14 h-14 ${b.bg} dark:bg-indigo-950/25 ${b.color} rounded-2xl flex items-center justify-center shadow-lg mb-8 group-hover:scale-110 group-hover:rotate-6 transition-all`}>
                            <b.icon size={28} />
                        </div>
                        <h3 className="text-xl font-black tracking-tight text-slate-900 dark:text-white mb-4">{b.title}</h3>
                        <p className="text-slate-500 dark:text-slate-400 font-medium leading-relaxed opacity-90">"{b.desc}"</p>
                    </div>
                ))}
            </div>

            {/* Comparison Matrix */}
            <div className="bg-white dark:bg-slate-900 rounded-[3rem] border border-slate-200 dark:border-slate-800 shadow-sm overflow-hidden mb-24">
                <div className="p-10 border-b border-slate-100 dark:border-slate-800 flex items-center gap-4">
                    <div className="p-3 bg-slate-950 dark:bg-slate-800 text-white rounded-2xl shadow-lg">
                        <Target size={24}/>
                    </div>
                    <div>
                        <h2 className="text-2xl font-black tracking-tight text-slate-900 dark:text-white">Tier Comparison</h2>
                        <p className="text-[10px] font-black text-slate-400 tracking-widest mt-1 uppercase">Logic Registry Access Matrix</p>
                    </div>
                </div>
                <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse">
                        <thead>
                            <tr className="bg-slate-50/50 dark:bg-slate-950/20">
                                <th className="py-6 px-10 text-[10px] font-black text-slate-400 tracking-widest uppercase">Protocol Feature</th>
                                <th className="py-6 px-6 text-center text-[10px] font-black text-slate-400 tracking-widest uppercase">Standard</th>
                                <th className="py-6 px-10 text-center text-[10px] font-black text-indigo-600 dark:text-indigo-400 tracking-widest bg-indigo-50/30 dark:bg-indigo-950/20 uppercase">Elite Pro</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                            {comparison.map((item, i) => {
                                const isFullAccess = item.feature === "Full Curriculum Access";
                                return (
                                    <tr key={i} className={`hover:bg-slate-50/50 dark:hover:bg-slate-850/50 transition-colors ${isFullAccess ? 'bg-emerald-500/[0.03] dark:bg-emerald-500/[0.01]' : ''}`}>
                                        <td className="py-5 px-10 font-bold text-slate-700 dark:text-slate-300 text-xs">
                                            <div className="flex flex-wrap items-center gap-2">
                                                <span>{item.feature}</span>
                                                {isFullAccess && (
                                                    <span className="px-2 py-0.5 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 font-black text-[8px] tracking-widest uppercase rounded">
                                                        Always 100% Free
                                                    </span>
                                                )}
                                            </div>
                                        </td>
                                        <td className="py-5 px-6 text-center">
                                            {typeof item.free === 'boolean' ? (
                                                item.free ? (
                                                    <Check size={18} className="mx-auto text-emerald-500" />
                                                ) : (
                                                    <X size={18} className="mx-auto text-slate-300 dark:text-slate-650" />
                                                )
                                            ) : (
                                                <span className="text-[10px] font-black text-slate-500 dark:text-slate-400">{item.free}</span>
                                            )}
                                        </td>
                                        <td className="py-5 px-10 text-center bg-indigo-50/25 dark:bg-indigo-950/10">
                                            {typeof item.pro === 'boolean' ? (
                                                item.pro ? (
                                                    <Star size={18} className="mx-auto text-indigo-500 fill-indigo-500" />
                                                ) : (
                                                    <X size={18} className="mx-auto text-slate-350 dark:text-slate-650" />
                                                )
                                            ) : (
                                                <span className="text-[11px] font-black text-indigo-600 dark:text-indigo-400">{item.pro}</span>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
                <div className="p-10 bg-slate-50 dark:bg-slate-950/30 flex justify-center border-t border-slate-100 dark:border-slate-800">
                    <button 
                        onClick={handleUpgradeNow}
                        className="px-16 py-5 bg-slate-950 dark:bg-indigo-600 text-white dark:hover:bg-indigo-700 rounded-2xl font-black text-xs tracking-widest shadow-2xl hover:bg-black transition-all active:scale-95 border-b-4 border-black dark:border-indigo-800"
                    >
                        Sync Elite Subscription
                    </button>
                </div>
            </div>

            {/* Bottom Proof Area */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
                <div className="space-y-8">
                    <h2 className="text-3xl font-black tracking-tight leading-none">Global Registry Support.</h2>
                    <p className="text-slate-500 font-medium leading-relaxed">
                        Diamond School Of Nursing is trusted by thousands of candidates across the United States. Our Elite Tier is engineered to minimize friction and maximize cognitive retention during the final 30 days before your board exam.
                    </p>
                    <div className="flex items-center gap-6">
                        <div className="flex -space-x-3">
                            <div className="w-10 h-10 rounded-full bg-indigo-100 border-2 border-white flex items-center justify-center text-[8px] font-black text-indigo-600 shadow-sm">FL</div>
                            <div className="w-10 h-10 rounded-full bg-emerald-100 border-2 border-white flex items-center justify-center text-[8px] font-black text-emerald-600 shadow-sm">TX</div>
                            <div className="w-10 h-10 rounded-full bg-rose-100 border-2 border-white flex items-center justify-center text-[8px] font-black text-rose-600 shadow-sm">CA</div>
                        </div>
                        <span className="text-[10px] font-black text-slate-400 tracking-widest">Active in 50 Jurisdictions</span>
                    </div>
                </div>
                <div className="p-8 bg-indigo-600 rounded-[2.5rem] text-white shadow-2xl relative overflow-hidden group">
                    <div className="absolute top-0 right-0 p-4 opacity-10 group-hover:rotate-12 transition-transform duration-700"><ShieldAlert size={120}/></div>
                    <div className="relative z-10 space-y-6">
                        <div className="flex items-center gap-3">
                            <div className="p-2 bg-white/20 backdrop-blur-md rounded-xl border border-white/20">
                                <Activity size={24} className="text-indigo-100" />
                            </div>
                            <h4 className="text-xl font-black tracking-tight">Standard Deviation</h4>
                        </div>
                        <p className="text-base font-bold text-indigo-100 leading-relaxed opacity-95">
                            "Candidates who utilize the Elite Tier metrics and the Mistake Vault consistently score 18% higher than the national average on their first attempt."
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};
